/**
 * Conversations / inbox threads — skeleton API.
 * Replace with GET/POST /conversations and related endpoints.
 * Seed data lives in mocks; this module is the only layer that imports mocks for chat.
 */
import { fetchInboxConversationDetail, isHumanAgentInboxLive } from "@/api/inboxAgentApi";
import { postReassignSession, postSendMessage } from "@/api/humanAgentOpsApi";
import {
  conversations as seedConversations,
  inboxCategories,
  teamInboxes,
} from "@/mocks/conversations";
import { materializeConversationSla } from "@/lib/materializeConversationSla";

const stubDelay = (ms = 45) => new Promise((r) => setTimeout(r, ms));

/** @param {Array<{ sentAt?: string, sender?: string }>} messages */
function mergeSlaMetaFromSentMessages(messages) {
  /** @type {Record<string, string>} */
  const meta = {};
  for (const m of messages) {
    if (!m?.sentAt) continue;
    if (m.sender === "user") {
      if (!meta.firstUserAt) meta.firstUserAt = m.sentAt;
      meta.lastUserAt = m.sentAt;
    }
    if (m.sender === "agent") {
      if (!meta.firstHumanAgentAt) meta.firstHumanAgentAt = m.sentAt;
      meta.lastHumanAgentAt = m.sentAt;
    }
  }
  return meta;
}

function deepCloneConversations(source) {
  try {
    return structuredClone(source);
  } catch {
    return JSON.parse(JSON.stringify(source));
  }
}

/** @type {typeof seedConversations} */
let skeletonStore = deepCloneConversations(seedConversations);

function filterConversationsByView(rows, view) {
  if (view === "all") return rows;
  if (view === "your-inbox") return rows.filter((c) => c.assignee === "Neelov");
  if (view === "unassigned") return rows.filter((c) => c.assignee === "Unassigned");
  if (view === "dashboard") return [];
  const teamInbox = teamInboxes.find((t) => t.id === view);
  if (teamInbox) {
    return rows.filter(
      (c) => c.team === teamInbox.label || c.team.startsWith(teamInbox.label.replace("...", ""))
    );
  }
  return rows;
}

/**
 * @param {{ view?: string }} [params] view = inbox category id (e.g. "all", "your-inbox", team inbox id)
 * @returns {Promise<{ data: typeof seedConversations }>}
 */
export async function getConversations(params = {}) {
  await stubDelay();
  const view = params.view ?? "all";
  const now = Date.now();
  const raw = filterConversationsByView(skeletonStore, view);
  return {
    data: raw.map((c) => {
      if (c.slaMetaLive && c.slaMeta?.firstUserAt) {
        return { ...c, slaMeta: { ...c.slaMeta } };
      }
      return materializeConversationSla({ ...c }, now);
    }),
  };
}

/**
 * Full graph for linking / history services (skeleton only).
 * Real backend would expose a dedicated identity or history API.
 */
export async function getConversationGraph() {
  await stubDelay(0);
  return { data: deepCloneConversations(skeletonStore) };
}

/**
 * Stub only: set conversation `status` in `skeletonStore` for mock inbox resolve/reopen. No-op when live.
 * @param {{ conversationId: string, status: 'open' | 'resolved' }} params
 */
export function patchStubSessionStatus({ conversationId, status }) {
  if (isHumanAgentInboxLive()) return;
  const conv = skeletonStore.find((c) => c.id === conversationId);
  if (!conv) return;
  const prev = conv.status;
  conv.status = status;
  if (prev === status) return;
  const iso = new Date().toISOString();
  const line =
    status === "resolved" ? "Conversation marked resolved." : "Conversation reopened.";
  conv.messages = [
    ...conv.messages,
    {
      id: `sys-stub-${status}-${Date.now()}`,
      sender: "system",
      type: "system",
      text: line,
      time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      sentAt: iso,
    },
  ];
}

/**
 * @param {{ id: string, workspaceId?: string | null }} params
 * @returns {Promise<{ data: (typeof seedConversations)[0] | null }>}
 */
export async function getConversation(params) {
  if (isHumanAgentInboxLive()) {
    const central = Boolean(params.centralInbox);
    const sessionWs =
      params.sessionWorkspaceId != null && String(params.sessionWorkspaceId).trim()
        ? String(params.sessionWorkspaceId).trim()
        : null;
    const { data } = await fetchInboxConversationDetail({
      id: params.id,
      workspaceId: central ? null : (params.workspaceId ?? null),
      scope: central ? "central" : undefined,
      sessionWorkspaceId: central ? sessionWs : null,
      signal: params.signal,
    });
    if (!data) return { data: null };
    const nowMs = Date.now();
    const slaFromMsgs = mergeSlaMetaFromSentMessages(data.messages ?? []);
    const merged = { ...data, slaMeta: { ...(data.slaMeta && typeof data.slaMeta === "object" ? data.slaMeta : {}), ...slaFromMsgs } };
    return { data: materializeConversationSla(merged, nowMs) };
  }
  await stubDelay(35);
  const row = skeletonStore.find((c) => c.id === params.id);
  if (!row) return { data: null };
  const now = Date.now();
  const cloned = deepCloneConversations([row])[0];
  if (cloned.slaMetaLive && cloned.slaMeta?.firstUserAt) {
    return { data: cloned };
  }
  return { data: materializeConversationSla(cloned, now) };
}

/**
 * @returns {Promise<{ data: { inboxCategories: typeof inboxCategories, teamInboxes: typeof teamInboxes } }>}
 */
export async function getInboxNavigation() {
  await stubDelay(25);
  return {
    data: {
      inboxCategories: [...inboxCategories],
      teamInboxes: [...teamInboxes],
    },
  };
}

/**
 * @param {{ conversationId: string, text: string, sender: 'agent' | 'note', workspaceId?: string | null, attachmentIds?: string[], attachments?: object[] }} params
 */
export async function postConversationMessage(params) {
  if (isHumanAgentInboxLive()) {
    const messageType = params.sender === "note" ? "note" : "chat";
    const trimmed = (params.text || "").trim();
    const result = await postSendMessage({
      sessionId: params.conversationId,
      message: trimmed,
      messageType,
      workspaceId: params.workspaceId ?? null,
      attachmentIds: params.attachmentIds,
    });
    // Shape the optimistic message the hook appends locally.
    const now = new Date();
    const displayText = trimmed || (params.attachmentIds?.length ? "[Attachment]" : "");
    return {
      data: {
        id: result?.message_id ? `hm-${result.message_id}` : `local-${Date.now()}`,
        sender: params.sender === "note" ? "note" : "agent",
        text: displayText,
        time: now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        type: "text",
        sentAt: now.toISOString(),
        canDelete: result?.can_delete === true,
        ephemeral: true,
        attachments: params.attachments,
        // Backend assign-on-reply: inbox + reporting use request_assignments.
        assignee: result?.assignee ?? null,
        assigneeAgentId: result?.assignee_agent_id ?? null,
      },
    };
  }
  await stubDelay(55);
  const conv = skeletonStore.find((c) => c.id === params.conversationId);
  if (!conv) throw new Error("Conversation not found.");
  const iso = new Date().toISOString();
  const msg = {
    id: `m${Date.now()}`,
    sender: params.sender,
    text: params.text.trim(),
    time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
    type: "text",
    sentAt: iso,
  };
  conv.messages = [...conv.messages, msg];

  if (!conv.slaMetaLive || !conv.slaMeta?.firstUserAt) {
    const mat = materializeConversationSla(conv, Date.now());
    conv.slaMeta = mat.slaMeta ? { ...mat.slaMeta } : {};
    conv.slaMetaLive = true;
  }
  const fromMessages = mergeSlaMetaFromSentMessages(conv.messages);
  conv.slaMeta = { ...conv.slaMeta, ...fromMessages };
  if (params.sender === "agent") {
    if (!conv.slaMeta.firstHumanAgentAt) conv.slaMeta.firstHumanAgentAt = iso;
    conv.slaMeta.lastHumanAgentAt = iso;
  }
  conv.slaMetaLive = true;

  return { data: msg };
}

/**
 * @param {{ conversationId: string, ticketId: string }} params
 */
export async function postTicketOpenedTimelineEvent(params) {
  await stubDelay(40);
  const conv = skeletonStore.find((c) => c.id === params.conversationId);
  if (!conv) throw new Error("Conversation not found.");
  const existingId = `sys-ticket-${params.ticketId}`;
  const already = conv.messages.find((m) => m.id === existingId);
  if (already) return { data: already };
  const msg = {
    id: existingId,
    sender: "system",
    type: "system",
    text: `Support ticket #${params.ticketId} was opened. A confirmation email with this ticket number was sent to the customer.`,
    time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
  };
  conv.messages = [...conv.messages, msg];
  return { data: msg };
}

/**
 * @param {{ conversationId: string, ticketId: string, statusLabel: string }} params
 */
export async function postTicketStatusChangedTimelineEvent(params) {
  await stubDelay(35);
  const conv = skeletonStore.find((c) => c.id === params.conversationId);
  if (!conv) throw new Error("Conversation not found.");
  const msg = {
    id: `sys-ticket-status-${params.ticketId}-${Date.now()}`,
    sender: "system",
    type: "system",
    text: `Ticket #${params.ticketId} was marked ${params.statusLabel}.`,
    time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
  };
  conv.messages = [...conv.messages, msg];
  return { data: msg };
}

/**
 * Transfer primary assignee and append a system timeline line.
 * In live mode: params must include toAgentId (int) and optionally workspaceId.
 * @param {{ conversationId: string, toAssignee?: string, toAgentId?: number, workspaceId?: string | null }} params
 * @returns {Promise<{ data: (typeof seedConversations)[0] }>}
 */
export async function transferConversation(params) {
  if (isHumanAgentInboxLive()) {
    if (!params.toAgentId) throw new Error("toAgentId is required in live mode");
    await postReassignSession({
      sessionId: params.conversationId,
      toAgentId: params.toAgentId,
      workspaceId: params.workspaceId ?? null,
    });
    // Reload the full thread so the new assignee and system pill appear.
    const { data } = await fetchInboxConversationDetail({
      id: params.conversationId,
      workspaceId: params.centralInbox ? null : (params.workspaceId ?? null),
      scope: params.centralInbox ? "central" : undefined,
      sessionWorkspaceId: params.centralInbox ? (params.sessionWorkspaceId ?? params.workspaceId ?? null) : null,
    });
    return { data };
  }
  await stubDelay(55);
  const conv = skeletonStore.find((c) => c.id === params.conversationId);
  if (!conv) throw new Error("Conversation not found.");
  const to = params.toAssignee.trim();
  if (!to) throw new Error("Assignee is required.");
  const from = conv.assignee ?? "Unassigned";
  if (from === to) {
    throw new Error("This conversation is already assigned to that agent.");
  }
  conv.assignee = to;
  const msg = {
    id: `sys-transfer-${Date.now()}`,
    sender: "system",
    type: "system",
    text: `Conversation transferred from ${from} to ${to}.`,
    time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
  };
  conv.messages = [...conv.messages, msg];
  const cloned = deepCloneConversations([conv])[0];
  if (cloned.slaMetaLive && cloned.slaMeta?.firstUserAt) {
    return { data: cloned };
  }
  return { data: materializeConversationSla(cloned, Date.now()) };
}
